import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './PackingListUpload.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const EMPTY_PACKING_LIST_FIELDS = {
  supplier_id: '',
  order_number: '',
  invoice_number: '',
  client_po_number: '',
  notes: '',
  pallet_mode: 'simple',
  file: null,
};

export default function PackingListUpload({ token }) {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeShipmentId, setActiveShipmentId] = useState(null);
  const [uploadedPackingLists, setUploadedPackingLists] = useState([]);
  const [refCheckMessage, setRefCheckMessage] = useState('');

  const [formData, setFormData] = useState({
    client_name: '',
    shesha_reference: '',
    sfr_number: '',
    bill_of_lading: '',
    vessel_voyage_number: '',
    container_count: '',
    container_type: 'FCL',
    gross_weight_lcl: '',
    delivery_address: '',
    ...EMPTY_PACKING_LIST_FIELDS,
  });

  const shipmentLocked = Boolean(activeShipmentId);

  const packingListReferencePreview = () => {
    const shesha = (formData.shesha_reference || '').trim();
    const invoice = (formData.invoice_number || '').trim();
    if (!shesha || !invoice) return '';
    return `${shesha}_${invoice}`;
  };

  useEffect(() => {
    if (token) {
      fetchSuppliers();
    }
  }, [token]);

  const fetchSuppliers = async () => {
    try {
      const response = await fetch(`${API_URL}/suppliers`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to load suppliers');

      const data = await response.json();
      setSuppliers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(`Error loading suppliers: ${err.message}`);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name === 'shesha_reference') {
      setRefCheckMessage('');
    }
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      file: e.target.files[0],
    }));
  };

  const handleContainerTypeChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      container_type: e.target.value,
    }));
  };

  const checkSheshaReference = async () => {
    const shesha = formData.shesha_reference.trim();
    if (!shesha) {
      setRefCheckMessage('Enter a Shesha Reference to check availability.');
      return;
    }
    try {
      const response = await fetch(
        `${API_URL}/shipments/check-reference?shesha_reference=${encodeURIComponent(shesha)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (!response.ok) {
        setRefCheckMessage(data.error || 'Could not verify reference');
        return;
      }
      setRefCheckMessage(
        data.exists
          ? (data.message || 'This Shesha Reference already exists in the system.')
          : 'Shesha Reference is available.'
      );
    } catch (err) {
      setRefCheckMessage('Could not verify reference.');
    }
  };

  const resetPackingListFields = () => {
    setFormData((prev) => ({
      ...prev,
      ...EMPTY_PACKING_LIST_FIELDS,
    }));
    const fileInput = document.getElementById('pdfFile');
    if (fileInput) fileInput.value = '';
  };

  const validateForm = () => {
    if (!formData.client_name || !formData.sfr_number) {
      return 'Client name and Client Ref/PO are required';
    }
    if (!formData.supplier_id) {
      return 'Supplier is required';
    }
    if (!formData.invoice_number.trim()) {
      return 'Invoice number is required';
    }
    if (!formData.file) {
      return 'PDF file is required';
    }
    if (!activeShipmentId && !formData.shesha_reference.trim()) {
      return 'Shesha Reference is required for a new shipment';
    }
    return null;
  };

  const buildFormDataToSend = () => {
    const formDataToSend = new FormData();
    formDataToSend.append('supplier_id', formData.supplier_id);
    formDataToSend.append('order_number', formData.order_number);
    formDataToSend.append('invoice_number', formData.invoice_number.trim());
    formDataToSend.append('client_po_number', formData.client_po_number);
    formDataToSend.append('notes', formData.notes);
    formDataToSend.append('pallet_mode', formData.pallet_mode);
    formDataToSend.append('file', formData.file);
    formDataToSend.append('client_name', formData.client_name);
    formDataToSend.append('sfr_number', formData.sfr_number);
    formDataToSend.append('bill_of_lading', formData.bill_of_lading);
    formDataToSend.append('vessel_voyage_number', formData.vessel_voyage_number);
    formDataToSend.append('container_count', formData.container_count);
    formDataToSend.append('container_type', formData.container_type);
    formDataToSend.append('gross_weight_lcl', formData.gross_weight_lcl);
    formDataToSend.append('delivery_address', formData.delivery_address);

    if (activeShipmentId) {
      formDataToSend.append('shipment_id', activeShipmentId);
    } else {
      formDataToSend.append('shesha_reference', formData.shesha_reference.trim());
    }
    return formDataToSend;
  };

  const uploadPackingList = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return null;
    }

    const response = await fetch(`${API_URL}/packing-lists/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: buildFormDataToSend(),
    });

    const data = await response.json();
    if (!response.ok) {
      setError(data.error || 'Failed to upload packing list');
      return null;
    }
    return data;
  };

  const handleAddAnother = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const data = await uploadPackingList();
      if (!data) {
        setLoading(false);
        return;
      }

      setActiveShipmentId(data.shipment_id);
      setUploadedPackingLists((prev) => [
        ...prev,
        {
          packing_list_id: data.packing_list_id,
          packing_list_reference: data.packing_list_reference,
          shesha_reference: data.shesha_reference,
        },
      ]);
      setFormData((prev) => ({
        ...prev,
        shesha_reference: data.shesha_reference || prev.shesha_reference,
      }));
      setSuccess(
        `Packing list ${data.packing_list_reference} uploaded. Add another packing list or finish recording the shipment.`
      );
      resetPackingListFields();
    } catch (err) {
      setError(`Error uploading packing list: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const data = await uploadPackingList();
      if (!data) {
        setLoading(false);
        return;
      }

      setSuccess('Shipment recorded and packing list uploaded successfully!');
      setTimeout(() => {
        navigate(`/packing-list/${data.packing_list_id}/cargo`);
      }, 1500);
    } catch (err) {
      setError(`Error uploading packing list: ${err.message}`);
      setLoading(false);
    }
  };

  return (
    <div className="packing-list-upload-container">
      <div className="upload-header">
        <h1>Record Inbound Shipment</h1>
        <p>Record shipment details and upload one or more packing list PDFs under the same Shesha Reference</p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {uploadedPackingLists.length > 0 && (
        <div className="uploaded-packing-lists">
          <h3>Packing lists on this shipment</h3>
          <ul>
            {uploadedPackingLists.map((pl) => (
              <li key={pl.packing_list_id}>
                <strong>{pl.packing_list_reference}</strong>
                <span className="pl-upload-meta">Shesha Ref: {pl.shesha_reference}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} className="upload-form">
        <div className="form-column">
          <fieldset className="form-section" disabled={shipmentLocked}>
            <legend>Shipment Details {shipmentLocked && '(locked)'}</legend>

            <div className="form-group">
              <label htmlFor="clientName">Client Name *</label>
              <input
                id="clientName"
                type="text"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                placeholder="e.g., ABC Industries"
                required
                disabled={shipmentLocked}
              />
            </div>

            <div className="form-group">
              <label htmlFor="sheshaRef">Shesha Reference *</label>
              <div className="shesha-ref-row">
                <input
                  id="sheshaRef"
                  type="text"
                  name="shesha_reference"
                  value={formData.shesha_reference}
                  onChange={handleChange}
                  placeholder="e.g., SR-2026-001"
                  required={!shipmentLocked}
                  disabled={shipmentLocked}
                />
                {!shipmentLocked && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={checkSheshaReference}>
                    Check
                  </button>
                )}
              </div>
              {refCheckMessage && <small className={refCheckMessage.includes('available') ? 'hint-ok' : 'hint-warn'}>{refCheckMessage}</small>}
              {shipmentLocked && (
                <small>Shipment is linked to Shesha Reference {formData.shesha_reference}</small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="sfrNumber">Client Ref/PO *</label>
              <input
                id="sfrNumber"
                type="text"
                name="sfr_number"
                value={formData.sfr_number}
                onChange={handleChange}
                placeholder="e.g., client reference number"
                required
                disabled={shipmentLocked}
              />
            </div>

            <div className="form-group">
              <label htmlFor="bol">Bill of Lading</label>
              <input
                id="bol"
                type="text"
                name="bill_of_lading"
                value={formData.bill_of_lading}
                onChange={handleChange}
                placeholder="e.g., BOL-789456"
                disabled={shipmentLocked}
              />
            </div>

            <div className="form-group">
              <label htmlFor="vesselVoyage">Vessel/Flight Number</label>
              <input
                id="vesselVoyage"
                type="text"
                name="vessel_voyage_number"
                value={formData.vessel_voyage_number}
                onChange={handleChange}
                placeholder="e.g., VESSEL-123/V456"
                disabled={shipmentLocked}
              />
            </div>

            <div className="form-group">
              <label htmlFor="containerCount">Container/Packages</label>
              <input
                id="containerCount"
                type="number"
                name="container_count"
                value={formData.container_count}
                onChange={handleChange}
                placeholder="e.g., 2"
                disabled={shipmentLocked}
              />
            </div>

            <div className="form-group">
              <label htmlFor="containerType">Shipping Type</label>
              <select
                id="containerType"
                name="container_type"
                value={formData.container_type}
                onChange={handleContainerTypeChange}
                disabled={shipmentLocked}
              >
                <option value="FCL">FCL (Full Container Load)</option>
                <option value="LCL">LCL (Less than Container Load)</option>
                <option value="Air Freight">Air Freight</option>
              </select>
            </div>

            {formData.container_type === 'LCL' && (
              <div className="form-group">
                <label htmlFor="grossWeight">Gross Weight (LCL) in kg</label>
                <input
                  id="grossWeight"
                  type="number"
                  name="gross_weight_lcl"
                  value={formData.gross_weight_lcl}
                  onChange={handleChange}
                  placeholder="e.g., 5000"
                  step="0.01"
                  disabled={shipmentLocked}
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="deliveryAddress">Delivery address, contact name and contact number</label>
              <textarea
                id="deliveryAddress"
                name="delivery_address"
                value={formData.delivery_address}
                onChange={handleChange}
                placeholder="Full delivery address, contact name, and contact number"
                rows="3"
                disabled={shipmentLocked}
              />
            </div>
          </fieldset>
        </div>

        <div className="form-column">
          <fieldset className="form-section">
            <legend>Packing List Details</legend>

            <div className="form-group">
              <label htmlFor="supplier">Supplier *</label>
              <select
                id="supplier"
                name="supplier_id"
                value={formData.supplier_id}
                onChange={handleChange}
                required
              >
                <option value="">-- Select Supplier --</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.supplier_id} value={supplier.supplier_id}>
                    {supplier.supplier_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="orderNumber">Shesha Note</label>
              <input
                id="orderNumber"
                type="text"
                name="order_number"
                value={formData.order_number}
                onChange={handleChange}
                placeholder="Internal info only"
              />
            </div>

            <div className="form-group">
              <label htmlFor="invoiceNumber">Invoice Number *</label>
              <input
                id="invoiceNumber"
                type="text"
                name="invoice_number"
                value={formData.invoice_number}
                onChange={handleChange}
                placeholder="e.g., INV-2026-001"
                required
              />
              {packingListReferencePreview() && (
                <small className="reference-preview">
                  Packing list reference: <strong>{packingListReferencePreview()}</strong>
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="clientPO">Supplier Ref/PO</label>
              <input
                id="clientPO"
                type="text"
                name="client_po_number"
                value={formData.client_po_number}
                onChange={handleChange}
                placeholder="e.g., PO-CLIENT-001"
              />
            </div>

            <div className="form-group">
              <label htmlFor="palletMode">Pallet tracking</label>
              <select
                id="palletMode"
                name="pallet_mode"
                value={formData.pallet_mode}
                onChange={handleChange}
              >
                <option value="simple">Simple — one material per pallet (default)</option>
                <option value="mixed">Mixed — item numbers identify pallets (supplier packing list)</option>
              </select>
            </div>

            <div className="notes-block">
              <div className="form-group">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Delivery instructions or notes for this packing list"
                  rows="4"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="pdfFile">Packing List PDF *</label>
              <input
                id="pdfFile"
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                required
              />
              {formData.file && <small>Selected: {formData.file.name}</small>}
            </div>
          </fieldset>
        </div>

        <div className="upload-actions">
          <button
            type="button"
            className="btn btn-secondary btn-large"
            onClick={handleAddAnother}
            disabled={loading}
          >
            {loading ? 'Uploading...' : 'Add Another Packing List'}
          </button>
          <button type="submit" className="btn btn-primary btn-large" disabled={loading}>
            {loading ? 'Uploading...' : 'Record Shipment & Upload PDF'}
          </button>
        </div>
      </form>
    </div>
  );
}
